// Kochsitzung und Timer im localStorage, je Rezept ein Schluessel "nb2.kochen.<rezept_id>" (W8, Astrid).
//
// Timer laufen weiter, wenn man vor- und zurueckblaettert, den Kochmodus verlaesst oder die Seite neu laedt:
// jeder Timer speichert nur endetUm (Epoch-ms) bzw. restMs (pausiert), nie einen heruntergezaehlten Zaehler
// (Lyra SPEZ 6.1). Die Sitzung (Schritt, Portionen, Haekchen) gilt 12 Std; laufende Timer halten sie am Leben.

export const PRAEFIX = 'nb2.kochen.';
const SITZUNG_GUELTIG_MS = 12 * 3600 * 1000;
export const MAX_TIMER = 6;

export type TimerStatus = 'laeuft' | 'pausiert' | 'klingelt' | 'fertig';

export interface KochTimer {
  id: string;
  rezeptId: string;
  rezeptSlug: string;
  rezeptName: string;
  /** Schritt (1-basiert) bei erkannten Timern, null bei eigenen */
  schritt: number | null;
  /** Index des erkannten Timers im Schritt */
  index: number | null;
  label: string;
  /** Gesamtdauer des laufenden Durchgangs (fuer den Fortschrittsbalken) */
  dauerMs: number;
  endetUm: number | null;
  restMs: number | null;
  status: TimerStatus;
  klingeltSeit: number | null;
  /** Ton aus (gedrueckt oder nach 60 s automatisch); der Chip bleibt "klingelt", bis man ihn antippt */
  stumm: boolean;
  /** Ton laeuft bis (Epoch-ms), 60 s ab dem Moment, in dem die App das Ende bemerkt */
  tonBis: number | null;
  bisMs: number | null;
  bedingung: string | null;
  seiten: number | null;
  seite: number;
  /** Erinnerungen an diesem Timer ("in den letzten 10 Min ...", "nach der Haelfte wenden") */
  erinnerungen: TimerErinnerung[];
  /** Erledigte Erinnerungen dieses Durchgangs (key) */
  erinnert: string[];
  gestartet: number;
}

export interface TimerErinnerung {
  key: string;
  restMs: number | null;
  haelfte: boolean;
  text: string;
}

export interface Kochsitzung {
  rezeptId: string;
  slug: string;
  name: string;
  inhaltVersion: number;
  portionen: number;
  /** 0 = Vorbereitung, 1..n = Schritt, n + 1 = Guten Appetit */
  schritt: number;
  abgehakt: string[];
  /** Mit -/+ verstellte Dauer noch nicht gestarteter Timer, Schluessel "<schritt>:<index>" */
  bereitMs: Record<string, number>;
  einstieg: string;
  gestartet: number;
  aktualisiert: number;
}

interface Eintrag {
  sitzung: Kochsitzung | null;
  timer: KochTimer[];
}

function lesen(rezeptId: string): Eintrag {
  try {
    const roh = localStorage.getItem(PRAEFIX + rezeptId);
    if (!roh) return { sitzung: null, timer: [] };
    const d = JSON.parse(roh) as Partial<Eintrag> | null;
    return { sitzung: d?.sitzung ?? null, timer: Array.isArray(d?.timer) ? d.timer : [] };
  } catch {
    return { sitzung: null, timer: [] };
  }
}

function schreiben(rezeptId: string, e: Eintrag): void {
  try {
    if (!e.sitzung && e.timer.length === 0) localStorage.removeItem(PRAEFIX + rezeptId);
    else localStorage.setItem(PRAEFIX + rezeptId, JSON.stringify(e));
  } catch {
    // Speicher voll oder privat: Kochmodus laeuft trotzdem, nur ohne Weiterkochen nach Neuladen
  }
}

function alleSchluessel(): string[] {
  const k: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const s = localStorage.key(i);
    if (s?.startsWith(PRAEFIX)) k.push(s.slice(PRAEFIX.length));
  }
  return k;
}

// ---------------------------------------------------------------- Sitzung

export function sitzungLesen(rezeptId: string, inhaltVersion: number): Kochsitzung | null {
  const { sitzung } = lesen(rezeptId);
  if (!sitzung) return null;
  const aktiv = lesen(rezeptId).timer.some((t) => t.status !== 'fertig');
  // Aelter als 12 Std oder Rezept inzwischen bearbeitet: verwerfen (Lyra SPEZ 6.3)
  if ((!aktiv && Date.now() - sitzung.aktualisiert > SITZUNG_GUELTIG_MS) || sitzung.inhaltVersion !== inhaltVersion) {
    sitzungSchreiben(rezeptId, null);
    return null;
  }
  return sitzung;
}

export function sitzungSchreiben(rezeptId: string, s: Kochsitzung | null): void {
  const e = lesen(rezeptId);
  e.sitzung = s ? { ...s, aktualisiert: Date.now() } : null;
  schreiben(rezeptId, e);
}

// ---------------------------------------------------------------- Timer

export function timerAlle(): KochTimer[] {
  return alleSchluessel().flatMap((id) => lesen(id).timer);
}

export function timerSetzen(rezeptId: string, liste: KochTimer[]): void {
  const e = lesen(rezeptId);
  e.timer = liste;
  schreiben(rezeptId, e);
}

export function timerVonRezept(rezeptId: string): KochTimer[] {
  return lesen(rezeptId).timer;
}

/** Beim Abmelden und Personenwechsel: alles Gekochte dieses Geraets weg (wie die Daten-Caches). */
export function kochenLeeren(): void {
  for (const id of alleSchluessel()) localStorage.removeItem(PRAEFIX + id);
}

// ---------------------------------------------------------------- Wisch-Hinweis (nur die ersten zwei Kochsitzungen)

const WISCH_ZAEHLER = 'nb2.kochen-wischhinweis';

export function wischHinweisZeigen(): boolean {
  return Number(localStorage.getItem(WISCH_ZAEHLER) ?? '0') < 2;
}

export function wischHinweisZaehlen(): void {
  localStorage.setItem(WISCH_ZAEHLER, String(Number(localStorage.getItem(WISCH_ZAEHLER) ?? '0') + 1));
}
